import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ActionCableSubscriptionStatus, dayjs } from '@repo/utils';
import { type LeadChatMessagesResponse } from 'chat/services/leadChatMessagesService';
import { type ChatMessage, DeliveryStatus } from 'chat/types/message';

export interface ChatWindowStateType {
  messages: ChatMessage[];
  status: ActionCableSubscriptionStatus;
  subscriptionId: string | null;
  messagesMeta?: LeadChatMessagesResponse['meta'];
  chatMeta?: {
    isCustomerSupportWindowActive?: boolean;
    unreadMessagesCount?: number;
  }
}

export interface ChatWindowStateBaseType {
  chatInstances: {
    [key: string]: ChatWindowStateType;
  };
}

const initialState: ChatWindowStateBaseType = {
  chatInstances: {},
};

export const chatWindowSlice = createSlice({
  name: 'chatWindowSlice',
  initialState,
  reducers: {
    initializeChatInstance: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      if (state.chatInstances[payload]) return;

      state.chatInstances[payload] = {
        messages: [],
        status: ActionCableSubscriptionStatus.UNINITIALIZED,
        subscriptionId: null,
      };
    },
    setSubscriptionId: (state, action: PayloadAction<{
      chatInstanceId: string;
      subscriptionId: string;
    }>) => {
      const { payload } = action;
      const { chatInstanceId, subscriptionId } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      state.chatInstances[chatInstanceId].subscriptionId = subscriptionId;
    },
    setSubscriptionStatus: (state, action: PayloadAction<{
      chatInstanceId: string;
      status: ActionCableSubscriptionStatus;
    }>) => {
      const { payload } = action;
      const { chatInstanceId, status } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      state.chatInstances[chatInstanceId] = {
        ...state.chatInstances[chatInstanceId],
        status,
      };
    },
    addMessages: (state, action: PayloadAction<{
      chatInstanceId: string;
      messages: ChatMessage[];
      prefix?: boolean;
    }>) => {
      const { payload } = action;
      const { chatInstanceId, messages, prefix = true } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      const existingMessageIds = new Set(state.chatInstances[chatInstanceId].messages.map((msg) => msg.id));
      const uniqueMessages = messages.filter((msg) => !existingMessageIds.has(msg.id));
      const sortedMessages = [...uniqueMessages].reverse();

      const currentMessages = state.chatInstances[chatInstanceId].messages;
      if (prefix) {
        state.chatInstances[chatInstanceId].messages = [...sortedMessages, ...currentMessages];
      } else {
        state.chatInstances[chatInstanceId].messages = [...currentMessages, ...sortedMessages];
      }
    },
    setMessagesMeta: (state, action: PayloadAction<{
      chatInstanceId: string;
      messagesMeta: LeadChatMessagesResponse['meta'];
    }>) => {
      const { payload } = action;
      const { chatInstanceId, messagesMeta } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      state.chatInstances[chatInstanceId].messagesMeta = messagesMeta;
    },
    setChatMeta: (state, action: PayloadAction<{
      chatInstanceId: string;
      chatMeta: ChatWindowStateBaseType['chatInstances'][string]['chatMeta'];
    }>) => {
      const { payload } = action;
      const { chatInstanceId, chatMeta } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      state.chatInstances[chatInstanceId].chatMeta = {
        ...state.chatInstances[chatInstanceId].chatMeta,
        ...chatMeta,
      };
    },
    updateMessageStatus: (state, action: PayloadAction<{
      chatInstanceId: string;
      messageId: string;
      status: DeliveryStatus;
      failureReason?: string;
    }>) => {
      const { payload } = action;
      const {
        chatInstanceId, messageId, status, failureReason,
      } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      const message = state.chatInstances[chatInstanceId].messages.find((_message) => _message.id === messageId);
      if (!message) return;

      if (status === DeliveryStatus.FAILED) {
        message.meta = {
          ...message.meta,
          failureReason,
        };
      }
      message.deliveryStatus = status;
    },
    replaceMessage: (state, action: PayloadAction<{
      chatInstanceId: string;
      messageId: string;
      message: ChatMessage;
    }>) => {
      const { payload } = action;
      const { chatInstanceId, messageId, message } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      const index = state.chatInstances[chatInstanceId].messages.findIndex((_message) => _message.id === messageId);
      if (index === -1) return;

      const currentUpdatedAt = dayjs(state.chatInstances[chatInstanceId].messages[index]!.updatedAt);
      const newUpdatedAt = dayjs(message.updatedAt);
      // Update only if the new message is newer
      if (newUpdatedAt.isAfter(currentUpdatedAt)) {
        state.chatInstances[chatInstanceId].messages[index] = message;
      }
    },
    incrementUnreadMessagesCount: (state, action: PayloadAction<{
      chatInstanceId: string;
    }>) => {
      const { payload } = action;
      const { chatInstanceId } = payload;
      if (!state.chatInstances[chatInstanceId]) return;

      const unreadMessagesCount = state.chatInstances[chatInstanceId].chatMeta?.unreadMessagesCount ?? 0;

      state.chatInstances[chatInstanceId].chatMeta = {
        ...state.chatInstances[chatInstanceId].chatMeta,
        unreadMessagesCount: unreadMessagesCount + 1,
      };
    },
  },
});

export const {
  addMessages,
  initializeChatInstance,
  setSubscriptionStatus,
  setSubscriptionId,
  setChatMeta,
  setMessagesMeta,
  updateMessageStatus,
  replaceMessage,
  incrementUnreadMessagesCount,
} = chatWindowSlice.actions;

export default chatWindowSlice.reducer;
